import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listBillingRules, createBillingRule, updateBillingRule,
  toggleBillingRule, deleteBillingRule, copyBillingRules,
  getBillingRuleCoverage,
} from "@/lib/billing-admin.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ArrowLeft, Pencil, Check, X, Copy, Eye, AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/billing/rules")({ component: BillingRulesPage });

const RULE_TYPES = [
  { v: "storage_per_bin_day", l: "Storage / occupied bin / day", group: "Storage" },
  { v: "per_order", l: "Per order shipped", group: "Outbound" },
  { v: "per_order_line", l: "Per order line", group: "Outbound" },
 { v: "per_pick", l: "Per unit picked", group: "Outbound" },
 { v: "per_pick_task", l: "Per completed pick task", group: "Outbound" },
  { v: "per_pack", l: "Per shipment packed (handling)", group: "Outbound" },
  { v: "per_unit_shipped", l: "Per unit shipped", group: "Outbound" },
  { v: "per_label", l: "Per shipping label", group: "Outbound" },
  { v: "freight_pass_through", l: "Freight pass-through (+ markup)", group: "Outbound" },
  { v: "consumable_pass_through", l: "Consumables pass-through (+ markup)", group: "Outbound" },
  { v: "per_receipt", l: "Per inbound receipt", group: "Inbound" },
  { v: "per_unit_received", l: "Per unit received", group: "Inbound" },
  { v: "per_return", l: "Per RMA received", group: "Returns" },
  { v: "per_kit_build", l: "Per kit built", group: "Value-add" },
  { v: "per_cycle_count", l: "Per cycle count posted", group: "Value-add" },
  { v: "monthly_account_fee", l: "Monthly account fee", group: "Account" },
  { v: "monthly_minimum", l: "Monthly minimum (top-up)", group: "Account" },
  { v: "custom_flat", l: "Custom flat (manual only)", group: "Account" },
] as const;
const tLabel = (t: string) => RULE_TYPES.find((r) => r.v === t)?.l ?? t;
const usesMarkup = (t: string) => t === "freight_pass_through" || t === "consumable_pass_through";
const usesThreshold = (t: string) => t === "monthly_minimum";

function BillingRulesPage() {
  const qc = useQueryClient();
  const { tenantId, customers } = useScope();
  const tenantCustomers = useMemo(() => customers.filter((c) => c.tenant_id === tenantId), [customers, tenantId]);
  const listFn = useServerFn(listBillingRules);
  const createFn = useServerFn(createBillingRule);
  const updateFn = useServerFn(updateBillingRule);
  const toggleFn = useServerFn(toggleBillingRule);
  const deleteFn = useServerFn(deleteBillingRule);
  const copyFn = useServerFn(copyBillingRules);
  const coverageFn = useServerFn(getBillingRuleCoverage);

  const { data: rules = [] } = useQuery({
    queryKey: ["billing-rules", tenantId], enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const [coverageMonths, setCoverageMonths] = useState(3);
  const { data: coverageData } = useQuery({
    queryKey: ["billing-rule-coverage", tenantId, coverageMonths],
    enabled: !!tenantId,
    queryFn: () => coverageFn({ data: { tenantId: tenantId!, monthsBack: coverageMonths } }),
  });

  const [filterCust, setFilterCust] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");

  const grouped = useMemo(() => {
    const filtered = (rules as any[]).filter((r) => {
      if (filterCust === "tenant" && r.customer_id !== null) return false;
      if (filterCust !== "all" && filterCust !== "tenant" && r.customer_id !== filterCust) return false;
      if (filterType !== "all" && r.rule_type !== filterType) return false;
      return true;
    });
    const groups = new Map<string, { name: string; customerId: string | null; rows: any[] }>();
    groups.set("__tenant__", { name: "Tenant default (all customers)", customerId: null, rows: [] });
    for (const c of tenantCustomers) groups.set(c.id, { name: c.name, customerId: c.id, rows: [] });
    for (const r of filtered) {
      const key = r.customer_id ?? "__tenant__";
      if (!groups.has(key)) continue;
      groups.get(key)!.rows.push(r);
    }
    return Array.from(groups.values()).filter((g) => g.rows.length > 0);
  }, [rules, filterCust, filterType, tenantCustomers]);

  // Create
  const [open, setOpen] = useState(false);
  const [cust, setCust] = useState<string>("");
  const [type, setType] = useState<string>("per_order");
  const [desc, setDesc] = useState(""); const [price, setPrice] = useState(""); const [uom, setUom] = useState("");
  const [markup, setMarkup] = useState(""); const [threshold, setThreshold] = useState(""); const [notes, setNotes] = useState("");
  const resetForm = () => { setCust(""); setType("per_order"); setDesc(""); setPrice(""); setUom(""); setMarkup(""); setThreshold(""); setNotes(""); };
  const create = async () => {
    if (!tenantId || !desc) return toast.error("Description required");
    const cents = price ? Math.round(parseFloat(price) * 100) : 0;
    try {
      await runServerFn(() => createFn({ data: {
        tenantId, customerId: cust || null, ruleType: type,
        description: desc, unitPriceCents: cents, uom: uom || null,
        markupPct: markup ? parseFloat(markup) : 0,
        thresholdCents: threshold ? Math.round(parseFloat(threshold) * 100) : null,
        notes: notes || null,
      } }), "Create rule");
      toast.success("Rule added"); setOpen(false); resetForm();
      qc.invalidateQueries({ queryKey: ["billing-rules"] });
    } catch { /* toast handled */ }
  };

  // Inline edit
  const [editId, setEditId] = useState<string | null>(null);
  const [eDesc, setEDesc] = useState(""); const [ePrice, setEPrice] = useState("");
  const [eMarkup, setEMarkup] = useState(""); const [eThreshold, setEThreshold] = useState("");
  const startEdit = (r: any) => {
    setEditId(r.id); setEDesc(r.description); setEPrice((r.unit_price_cents/100).toFixed(2));
    setEMarkup(r.markup_pct ? String(r.markup_pct) : "");
    setEThreshold(r.threshold_cents ? (r.threshold_cents/100).toFixed(2) : "");
  };
  const saveEdit = async (r: any) => {
    try {
      await runServerFn(() => updateFn({ data: {
        ruleId: r.id, description: eDesc,
        unitPriceCents: Math.round(parseFloat(ePrice || "0") * 100),
        markupPct: eMarkup ? parseFloat(eMarkup) : 0,
        thresholdCents: eThreshold ? Math.round(parseFloat(eThreshold) * 100) : null,
      } }), "Save rule");
      setEditId(null); toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["billing-rules"] });
    } catch { /* toast handled */ }
  };

  const toggle = async (id: string, val: boolean) => {
    try {
      await runServerFn(() => toggleFn({ data: { ruleId: id, active: val } }), "Toggle rule");
      qc.invalidateQueries({ queryKey: ["billing-rules"] });
    } catch { /* toast handled */ }
  };
  const remove = async (id: string) => {
    try {
      await runServerFn(() => deleteFn({ data: { ruleId: id } }), "Delete rule");
      qc.invalidateQueries({ queryKey: ["billing-rules"] });
    } catch { /* toast handled */ }
  };

  // Copy schedule
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState(""); const [copyTo, setCopyTo] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);
  const doCopy = async () => {
    if (!tenantId || !copyFrom || !copyTo || copyFrom === copyTo) return toast.error("Pick different source and target");
    setCopyBusy(true);
    try {
      const res = await runServerFn(() => copyFn({ data: {
        tenantId,
        sourceCustomerId: copyFrom === "tenant" ? null : copyFrom,
        targetCustomerId: copyTo === "tenant" ? null : copyTo,
      } }), "Copy schedule");
      if (res.copied === 0) { toast.error("No active rules to copy"); return; }
      toast.success(`Copied ${res.copied} rule(s)`); setCopyOpen(false); setCopyFrom(""); setCopyTo("");
      qc.invalidateQueries({ queryKey: ["billing-rules"] });
    } catch { /* toast handled */ } finally { setCopyBusy(false); }
  };

  return (
    <>
      <PageHeader
        title="Billing Rules (Legacy)"
        description="Legacy per-customer fee schedule. New contracts should be authored in the Billing Platform — Contract."
        actions={<div className="flex gap-2"><ScopeBar /><HelpButton moduleKey="billing.rules" /><Button variant="outline" size="sm" asChild><Link to="/app/admin/billing-platform">Billing Platform</Link></Button><Button variant="outline" size="sm" asChild><Link to="/app/billing"><ArrowLeft className="h-4 w-4 mr-2" />Invoices</Link></Button></div>} />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Legacy surface — superseded by Sprint 32 Billing Platform.</div>
            <div className="text-muted-foreground">
              These rules write to <code>billing_rules</code> and are kept for historical invoices. New customer
              contracts (tiered rates, free allowances, monthly minimums, effective dates) belong in{" "}
              <Link to="/app/admin/billing-platform" className="underline">Billing Platform → Contract</Link>,
              which writes to <code>billing_policies</code> and the append-only <code>billing_ledger</code>.
            </div>
          </div>
        </div>
        <ContextualTip tipKey="billing_rules_intro" />
        <Tabs defaultValue="rules">
          <TabsList>
            <TabsTrigger value="rules">Rules</TabsTrigger>
            <TabsTrigger value="coverage">Coverage</TabsTrigger>
          </TabsList>
          <TabsContent value="rules" className="space-y-4 mt-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">Customer</Label>
            <Select value={filterCust} onValueChange={setFilterCust}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="tenant">Tenant default only</SelectItem>
                {tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {RULE_TYPES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex gap-2">
            <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline"><Copy className="h-4 w-4 mr-2" />Copy schedule</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Copy rate schedule</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2"><Label>From</Label>
                    <Select value={copyFrom} onValueChange={setCopyFrom}>
                      <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tenant">Tenant default</SelectItem>
                        {tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>To</Label>
                    <Select value={copyTo} onValueChange={setCopyTo}>
                      <SelectTrigger><SelectValue placeholder="Target" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tenant">Tenant default</SelectItem>
                        {tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">All active rules from the source will be cloned to the target.</p>
                </div>
                <DialogFooter><Button onClick={doCopy} disabled={copyBusy}>Copy</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" />New rule</Button></DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Create billing rule</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Customer (optional)</Label>
                      <Select value={cust} onValueChange={setCust}>
                        <SelectTrigger><SelectValue placeholder="All customers in tenant" /></SelectTrigger>
                        <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Rule type</Label>
                      <Select value={type} onValueChange={setType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{RULE_TYPES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Description (appears on invoice)</Label>
                    <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Pick & pack fee" /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-2"><Label>Unit price (USD)</Label>
                      <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
                    <div className="space-y-2"><Label>UoM (optional)</Label>
                      <Input value={uom} onChange={(e) => setUom(e.target.value)} placeholder="each, hr, lb" /></div>
                    {usesMarkup(type) && (
                      <div className="space-y-2"><Label>Markup %</Label>
                        <Input type="number" step="0.01" value={markup} onChange={(e) => setMarkup(e.target.value)} placeholder="15" /></div>
                    )}
                    {usesThreshold(type) && (
                      <div className="space-y-2"><Label>Minimum (USD)</Label>
                        <Input type="number" step="0.01" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="500" /></div>
                    )}
                  </div>
                  <div className="space-y-2"><Label>Internal notes</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
                </div>
                <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {grouped.length === 0 && <div className="border rounded-lg bg-card p-8 text-center text-muted-foreground text-sm">No rules match these filters.</div>}

        {grouped.map((g) => (
          <div key={g.customerId ?? "__tenant__"} className="border rounded-lg bg-card">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="font-semibold">{g.name} <Badge variant="outline" className="ml-2">{g.rows.length}</Badge></div>
              {g.customerId && (
                <Button size="sm" variant="ghost" asChild>
                  <Link to="/app/billing/customers/$customerId" params={{ customerId: g.customerId }}><Eye className="h-4 w-4 mr-2" />Rate card</Link>
                </Button>
              )}
            </div>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Markup</TableHead><TableHead className="text-right">Min</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {g.rows.map((r: any) => editId === r.id ? (
                  <TableRow key={r.id}>
                    <TableCell><Badge variant="outline">{tLabel(r.rule_type)}</Badge></TableCell>
                    <TableCell><Input value={eDesc} onChange={(e) => setEDesc(e.target.value)} /></TableCell>
                    <TableCell className="w-28"><Input type="number" step="0.01" value={ePrice} onChange={(e) => setEPrice(e.target.value)} /></TableCell>
                    <TableCell className="w-24">{usesMarkup(r.rule_type) ? <Input type="number" step="0.01" value={eMarkup} onChange={(e) => setEMarkup(e.target.value)} /> : "—"}</TableCell>
                    <TableCell className="w-28">{usesThreshold(r.rule_type) ? <Input type="number" step="0.01" value={eThreshold} onChange={(e) => setEThreshold(e.target.value)} /> : "—"}</TableCell>
                    <TableCell><Switch checked={r.active} onCheckedChange={(v) => toggle(r.id, v)} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => saveEdit(r)}><Check className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={r.id}>
                    <TableCell><Badge variant="outline">{tLabel(r.rule_type)}</Badge></TableCell>
                    <TableCell>{r.description}{r.uom && <span className="text-xs text-muted-foreground"> / {r.uom}</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">${(r.unit_price_cents / 100).toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums">{usesMarkup(r.rule_type) ? `${r.markup_pct ?? 0}%` : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{usesThreshold(r.rule_type) && r.threshold_cents != null ? `$${(r.threshold_cents/100).toFixed(2)}` : "—"}</TableCell>
                    <TableCell><Switch checked={r.active} onCheckedChange={(v) => toggle(r.id, v)} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(r)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          </div>
        ))}
          </TabsContent>
          <TabsContent value="coverage" className="space-y-4 mt-4">
            <div className="flex items-end gap-3">
              <div className="space-y-1"><Label className="text-xs">Window</Label>
                <Select value={String(coverageMonths)} onValueChange={(v) => setCoverageMonths(Number(v))}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last 1 month</SelectItem>
                    <SelectItem value="3">Last 3 months</SelectItem>
                    <SelectItem value="6">Last 6 months</SelectItem>
                    <SelectItem value="12">Last 12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {coverageData && coverageData.unmatched_lines > 0 && (
                <div className="ml-auto text-xs text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  {coverageData.unmatched_lines} invoice line(s) without a rule (${(coverageData.unmatched_amount_cents/100).toFixed(2)})
                </div>
              )}
            </div>
            <div className="border rounded-lg bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Rule</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Lines</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Customers</TableHead>
                  <TableHead>Last fired</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(coverageData?.coverage ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No rules.</TableCell></TableRow>
                  )}
                  {(coverageData?.coverage ?? [])
                    .slice()
                    .sort((a: any, b: any) => b.amount_cents - a.amount_cents)
                    .map((r: any) => {
                      const dormant = r.active && r.lines_count === 0;
                      return (
                        <TableRow key={r.rule_id} className={dormant ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}>
                          <TableCell className="font-medium">{r.description}</TableCell>
                          <TableCell><Badge variant="outline">{tLabel(r.rule_type)}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.customer_id ? "Customer" : "Tenant default"}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.lines_count.toLocaleString()}</TableCell>
                          <TableCell className="text-right tabular-nums">${(r.amount_cents/100).toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.customers_count}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.last_fired_at ? format(new Date(r.last_fired_at), "MMM d, yyyy") : "—"}</TableCell>
                          <TableCell>
                            {!r.active ? <Badge variant="outline">disabled</Badge>
                              : dormant ? <Badge variant="outline" className="border-amber-500 text-amber-700">dormant</Badge>
                              : <Badge variant="outline" className="border-emerald-500 text-emerald-700">active</Badge>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
